import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eraser, Loader2, ShieldCheck, AlertTriangle, FileText } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OFFENSE_OPTIONS = [
    { value: "misdemeanor", label: "Misdemeanor" },
    { value: "felony", label: "Felony (non-violent)" },
    { value: "arrest_only", label: "Arrest only — no conviction" },
    { value: "juvenile", label: "Juvenile offense" },
    { value: "drug_possession", label: "Drug possession" },
    { value: "other", label: "Other / Not sure" },
];

const ELIGIBILITY_COLOR = {
    likely_eligible: "#00FF85",
    likely_ineligible: "#FF3B30",
    complicated: "#FFCC00",
    insufficient_info: "#888888",
};

export default function Expungement() {
    const [form, setForm] = useState({
        state: "",
        offense_type: "misdemeanor",
        offense_description: "",
        conviction_year: "",
        time_since_completion_years: "",
        completed_sentence: true,
        new_offenses_since: false,
        pending_charges: false,
        full_name: "",
        contact_email: "",
    });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.state.trim() || form.state.trim().length < 2) {
            toast.error("Please enter your U.S. state");
            return;
        }
        if (form.offense_description.trim().length < 25) {
            toast.error("Please describe the offense in at least one full sentence");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const payload = {
                ...form,
                conviction_year: form.conviction_year ? Number(form.conviction_year) : null,
                time_since_completion_years: form.time_since_completion_years
                    ? Number(form.time_since_completion_years)
                    : null,
            };
            const { data } = await axios.post(`${API}/tools/expungement`, payload);
            setResult(data);
            toast.success("Eligibility analysis ready");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Analysis failed. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setResult(null);
    };

    return (
        <div className="min-h-screen bg-black text-white" data-testid="expungement-page">
            <Header />
            <main className="max-w-[1280px] mx-auto px-6 sm:px-10 py-14">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 flex items-center gap-2">
                    <Eraser size={11} /> HPM TOOLS · RECORD CLEARING
                </div>
                <h1 className="font-anton text-5xl sm:text-6xl uppercase leading-[0.95] mb-4">
                    Can your record be erased?
                </h1>
                <p className="font-sans text-base sm:text-lg text-white/75 leading-relaxed max-w-3xl mb-10">
                    Answer 7 questions. Our AI runs your state&apos;s expungement and
                    record-sealing statutes against your case and returns a plain-English
                    eligibility read with citations and next steps. Free. No card.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10">
                    <form
                        onSubmit={submit}
                        className="border-2 border-white/20 p-6 sm:p-8 space-y-5"
                        data-testid="expungement-form"
                    >
                        <Field label="State (US)" required>
                            <input
                                value={form.state}
                                onChange={(e) => update("state", e.target.value)}
                                placeholder="California"
                                data-testid="expungement-state"
                                className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm uppercase focus:border-[#FFCC00] outline-none"
                            />
                        </Field>

                        <Field label="Type of offense" required>
                            <select
                                value={form.offense_type}
                                onChange={(e) => update("offense_type", e.target.value)}
                                data-testid="expungement-offense-type"
                                className="w-full bg-black border-2 border-white/30 px-3 py-2 font-mono text-sm uppercase focus:border-[#FFCC00] outline-none"
                            >
                                {OFFENSE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Brief description of the offense" required>
                            <textarea
                                value={form.offense_description}
                                onChange={(e) => update("offense_description", e.target.value)}
                                rows={4}
                                placeholder="e.g. Misdemeanor petty theft from a retail store, no force used, value $87. First offense."
                                data-testid="expungement-description"
                                className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-sans text-sm focus:border-[#FFCC00] outline-none"
                            />
                        </Field>

                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Conviction year">
                                <input
                                    type="number"
                                    value={form.conviction_year}
                                    onChange={(e) => update("conviction_year", e.target.value)}
                                    placeholder="2018"
                                    data-testid="expungement-conviction-year"
                                    className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-[#FFCC00] outline-none"
                                />
                            </Field>
                            <Field label="Years since sentence ended">
                                <input
                                    type="number"
                                    step="0.5"
                                    value={form.time_since_completion_years}
                                    onChange={(e) => update("time_since_completion_years", e.target.value)}
                                    placeholder="3.5"
                                    data-testid="expungement-time-since"
                                    className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-[#FFCC00] outline-none"
                                />
                            </Field>
                        </div>

                        <div className="space-y-3 pt-2">
                            <Toggle
                                label="I completed all sentencing requirements (prison, probation, fines)"
                                value={form.completed_sentence}
                                onChange={(v) => update("completed_sentence", v)}
                                testid="expungement-completed-sentence"
                            />
                            <Toggle
                                label="I have new criminal offenses since this case"
                                value={form.new_offenses_since}
                                onChange={(v) => update("new_offenses_since", v)}
                                testid="expungement-new-offenses"
                            />
                            <Toggle
                                label="I have charges pending right now"
                                value={form.pending_charges}
                                onChange={(v) => update("pending_charges", v)}
                                testid="expungement-pending-charges"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="expungement-submit"
                            className="w-full bg-[#FFCC00] text-black px-6 py-3 font-anton text-xl uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                        >
                            {busy ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
                            {busy ? "Analysing…" : "Check eligibility"}
                        </button>

                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 leading-relaxed">
                            INFORMATIONAL AI ANALYSIS — NOT LEGAL ADVICE. CONSULT A LICENSED ATTORNEY
                            BEFORE FILING ANY PETITION.
                        </p>
                    </form>

                    <div className="border-2 border-white/20 p-6 sm:p-8" data-testid="expungement-result">
                        {!result ? (
                            <div className="h-full flex flex-col items-start justify-center gap-4 text-white/40 font-mono text-[11px] uppercase tracking-[0.25em]">
                                <FileText size={32} className="text-white/30" />
                                FILL THE FORM ·<br />ELIGIBILITY ANALYSIS APPEARS HERE
                            </div>
                        ) : (
                            <ResultCard result={result} onReset={reset} />
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">
                {label}{required && <span className="text-[#FF3B30]"> *</span>}
            </div>
            {children}
        </label>
    );
}

function Toggle({ label, value, onChange, testid }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer" data-testid={testid}>
            <button
                type="button"
                onClick={() => onChange(!value)}
                className={`w-12 h-6 border-2 transition-colors flex items-center px-0.5 ${value ? "bg-[#FFCC00] border-[#FFCC00] justify-end" : "bg-transparent border-white/40 justify-start"}`}
            >
                <span className={`block w-4 h-4 ${value ? "bg-black" : "bg-white/40"}`} />
            </button>
            <span className="font-sans text-sm text-white/80">{label}</span>
        </label>
    );
}

function ResultCard({ result, onReset }) {
    const r = result?.result || {};
    const eligibility = r.eligibility || "insufficient_info";
    const color = ELIGIBILITY_COLOR[eligibility] || "#888";
    const label = eligibility.replace(/_/g, " ").toUpperCase();
    return (
        <div className="space-y-5">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                    AI ELIGIBILITY READ
                </div>
                <div
                    className="font-anton text-4xl uppercase leading-none"
                    style={{ color }}
                    data-testid="expungement-eligibility"
                >
                    {label}
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mt-2">
                    CONFIDENCE: {r.confidence_pct ?? "—"}%
                </div>
            </div>

            {r.reasoning && (
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">REASONING</div>
                    <p className="font-sans text-sm text-white/85 leading-relaxed" data-testid="expungement-reasoning">{r.reasoning}</p>
                </div>
            )}

            {Array.isArray(r.controlling_statutes) && r.controlling_statutes.length > 0 && (
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">CONTROLLING STATUTES</div>
                    <ul className="font-mono text-[12px] text-white/85 space-y-1">
                        {r.controlling_statutes.map((s, i) => (
                            <li key={i}>· {s}</li>
                        ))}
                    </ul>
                </div>
            )}

            {Array.isArray(r.next_steps) && r.next_steps.length > 0 && (
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">NEXT STEPS</div>
                    <ol className="font-sans text-sm text-white/85 space-y-2 list-decimal list-inside">
                        {r.next_steps.map((s, i) => (
                            <li key={i}>{s}</li>
                        ))}
                    </ol>
                </div>
            )}

            {Array.isArray(r.forms_or_petitions) && r.forms_or_petitions.length > 0 && (
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">FORMS / PETITIONS</div>
                    <ul className="font-mono text-[12px] text-white/85 space-y-1">
                        {r.forms_or_petitions.map((s, i) => (
                            <li key={i}>· {s}</li>
                        ))}
                    </ul>
                </div>
            )}

            {r.estimated_timeline_months && (
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">ESTIMATED TIMELINE</div>
                    <div className="font-anton text-2xl">{r.estimated_timeline_months} MONTHS</div>
                </div>
            )}

            {Array.isArray(r.red_flags) && r.red_flags.length > 0 && (
                <div className="border-2 border-[#FF3B30] p-4 bg-[#FF3B30]/5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] mb-2 flex items-center gap-2">
                        <AlertTriangle size={11} /> RED FLAGS
                    </div>
                    <ul className="font-sans text-sm text-white/85 space-y-1">
                        {r.red_flags.map((s, i) => (
                            <li key={i}>· {s}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="pt-4 border-t border-white/10">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 leading-relaxed">
                    {result.disclaimer}
                </p>
                <button
                    onClick={onReset}
                    data-testid="expungement-reset"
                    className="mt-4 text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 hover:text-[#FFCC00]"
                >
                    ← Run another check
                </button>
            </div>
        </div>
    );
}
